import React, { useEffect } from "react";
import styled from "styled-components";
import { SectionHeader, Tooltip, TableAlerts } from "../../../../app/components";
import Typography from "@material-ui/core/Typography";
import InputBase from "@material-ui/core/InputBase";
import { withStyles } from "@material-ui/core/styles";
import SeparateContainer from "../../../../app/components/separate.Container";
import SideBySide from "../../../../app/components/sideBySide";
import { useActivationProfilesValidation } from "../../../../app/hooks/activation.profiles/useActivationProfilesValidation";

const StyledInputBase = withStyles(() => ({
  root: {
    borderRadius: "0px",
    border: "1px solid var(--teal-600)",
    padding: "0px 0px",
    width: "90%",
    "& .MuiInputBase-input": {
      textAlign: "center",
      "-moz-appearance": "textfield", // For Firefox
      "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
        // For Chrome and Safari
        "-webkit-appearance": "none",
        margin: 0,
      },
    },
  },
}))(InputBase);

export default function EditActivationProfiles({
  sitegroup,
  onChange,
  tooltip,
  setHasErrors,
}) {
  const { activationProfiles } = sitegroup;
  
  const { alerts } = useActivationProfilesValidation(activationProfiles);

  useEffect(() => {
    const activeAlerts = alerts.filter(alert => alert.condition);
    const hasErrorsValue = activeAlerts.length > 0;
    setHasErrors(hasErrorsValue);
  }, [alerts, setHasErrors]);

  // Function to handle blur with auto-complete logic
  const handleBlurWithAutoComplete = (index, field, value) => {
    // If user entered a value and the other field is empty, set it to 0
    if (value && value !== "" && value !== null) {
      const currentProfile = activationProfiles[index];
      const otherField = field === "siteActivationProfilePct" ? "siteActivationProfileDays" : "siteActivationProfilePct";
      const otherValue = currentProfile[otherField];
      
      // If the other field is empty, set it to 0
      if (otherValue === null || otherValue === undefined || otherValue === "") {
        onChange(index, otherField, "0");
      }
    }
  };

  return (
    <Layout>
      <SeparateContainer>
        <SideBySide>
          <SectionHeader
            hasStartAdornment={false}
            marginBottom={false}
            paddingBottom={false}
            endAdornment={
              tooltip && (
                <Tooltip
                  changeColor={true}
                  info={tooltip}
                />
              )
            }
          >
            Site Group Activation Profiles
          </SectionHeader>
        </SideBySide>
        <TableAlerts alerts={alerts} />
        <div style={{ padding: "10px 20px 20px 20px" }}>
          <Table>
            <Row>
              <Cell variant="header">
                <Typography align="right"># of Sites</Typography>
              </Cell>
              {activationProfiles.map((item, index) => (
                <Cell key={`Pct-${index}`}>
                  <StyledInputBase
                    type="number"
                    value={item.siteActivationProfilePct ?? ""}
                    onChange={({ target }) => {
                      onChange(index, "siteActivationProfilePct", target.value);
                    }}
                    onBlur={({ target }) => {
                      handleBlurWithAutoComplete(index, "siteActivationProfilePct", target.value);
                    }}
                    onInput={(e) => {
                      let value = e.target.value;
                      value = value.replace(/[.-]/g, '');
                      value = value.replace(/^0+/, '') || '0';
                      e.target.value = value;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === '.' || e.key === '-' || e.key === 'e' || e.key === 'E') {
                        e.preventDefault();
                      }
                    }}
                  />
                </Cell>
              ))}
            </Row>
            <Row>
              <Cell variant="header">
                <Typography align="right">Days</Typography>
              </Cell>
              {activationProfiles.map((item, index) => (
                <Cell key={`Days-${index}`}>
                  <StyledInputBase
                    type="number"
                    value={item.siteActivationProfileDays ?? ""}
                    onChange={({ target }) =>
                      onChange(index, "siteActivationProfileDays", target.value)
                    }
                    onBlur={({ target }) => {
                      handleBlurWithAutoComplete(index, "siteActivationProfileDays", target.value);
                    }}
                  />
                </Cell>
              ))}
            </Row>
          </Table>
        </div>
      </SeparateContainer>
    </Layout>
  );
}

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 1em;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  height: 50px;
  border-bottom: 1px solid var(--surface-300);

  &:last-child {
    border: none;
  }
`;

const Cell = styled.div`
  background: ${(p) =>
    p.variant === "header" ? "var(--surface-50)" : "white"};
  height: 100%;
  align-items: center;
  justify-content: center;
  display: flex;
  border-right: 0px solid var(--surface-300);
  &:last-child {
    border: none;
  }
  &:first-child {
    justify-content: flex-end;
    padding-right: 1em;
  }
`;

const Table = styled.div`
  display: grid;
  grid-template-rows: auto auto;
  border: 1px solid var(--surface-300);
  border-radius: 4px;
`;
